import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { getUrl } from 'aws-amplify/storage';
import { fetchUserAttributes, getCurrentUser } from 'aws-amplify/auth';
import { listRecipes, listIngredients, listSteps } from '../graphql/queries';
import { deleteRecipe, deleteIngredient, deleteStep } from '../graphql/mutations';

const client = generateClient();

export default function MyRecipes() {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [images, setImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const attrs = await fetchUserAttributes();
        // ownerは "sub::username" 形式で保存される
        const currentUser = await getCurrentUser();
        const username = currentUser.username || '';
        const sub = attrs.sub || '';
        const ownerVal = `${sub}::${username}`;
        const result = await client.graphql({
          query: listRecipes,
          variables: { filter: { owner: { eq: ownerVal } }, limit: 100 }
        });
        const items = result.data.listRecipes.items
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setRecipes(items);

        const imgMap = {};
        await Promise.all(items.map(async (r) => {
          if (r.imageUrl) {
            try {
              const urlResult = await getUrl({ key: r.imageUrl });
              imgMap[r.id] = urlResult.url.toString();
            } catch (e) {}
          }
        }));
        setImages(imgMap);
      } catch (err) {
        console.error('取得エラー:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const handleDelete = async (e, recipeId) => {
    e.stopPropagation(); // カード全体のクリックを防ぐ
    if (!window.confirm('このレシピを削除しますか？')) return;
    try {
      const [ingRes, stepRes] = await Promise.all([
        client.graphql({ query: listIngredients, variables: { filter: { recipeId: { eq: recipeId } }, limit: 100 } }),
        client.graphql({ query: listSteps, variables: { filter: { recipeId: { eq: recipeId } }, limit: 100 } }),
      ]);
      for (const ing of ingRes.data.listIngredients.items) {
        await client.graphql({ query: deleteIngredient, variables: { input: { id: ing.id } } });
      }
      for (const step of stepRes.data.listSteps.items) {
        await client.graphql({ query: deleteStep, variables: { input: { id: step.id } } });
      }
      await client.graphql({ query: deleteRecipe, variables: { input: { id: recipeId } } });
      setRecipes(prev => prev.filter(r => r.id !== recipeId));
    } catch (err) {
      console.error('削除エラー:', err);
      alert('削除に失敗しました');
    }
  };

  return (
    <div style={S.page}>
      <div style={S.container}>
        <div style={S.topBar}>
          <h1 style={S.heading}>📖 Myレシピ</h1>
          <button onClick={() => navigate('/recipe/create')} style={S.createBtn}>＋ 作成</button>
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', color: '#95a5a6', padding: '40px' }}>読み込み中...</p>
        ) : recipes.length === 0 ? (
          <div style={S.empty}>
            <p style={{ fontSize: '40px', margin: '0 0 10px' }}>📝</p>
            <p style={{ color: '#95a5a6' }}>まだレシピがありません</p>
            <button onClick={() => navigate('/recipe/create')} style={S.createBtn}>最初のレシピを作る</button>
          </div>
        ) : (
          <div style={S.grid}>
            {recipes.map(recipe => (
              <div key={recipe.id} style={S.card}>
                <div style={S.thumbWrap} onClick={() => navigate(`/recipe/${recipe.id}`)}>
                  {images[recipe.id]
                    ? <img src={images[recipe.id]} alt={recipe.title} style={S.thumb} />
                    : <div style={S.thumbPlaceholder}>🍳</div>
                  }
                  <div style={{ ...S.badge, background: recipe.isPublic ? '#27ae60' : '#95a5a6' }}>
                    {recipe.isPublic ? '公開' : '非公開'}
                  </div>
                  {/* 削除ボタン */}
                  <button onClick={e => handleDelete(e, recipe.id)} style={S.deleteBtn}>🗑️</button>
                </div>
                <p style={S.cardTitle} onClick={() => navigate(`/recipe/${recipe.id}`)}>{recipe.title}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const S = {
  page: { padding: '80px 20px 40px', backgroundColor: '#f4f7f9', minHeight: '100vh' },
  container: { maxWidth: '600px', margin: '0 auto' },
  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' },
  heading: { fontSize: '20px', fontWeight: '900', color: '#2c3e50', margin: 0 },
  createBtn: {
    padding: '10px 18px', borderRadius: '20px', border: 'none',
    background: '#e74c3c', color: '#fff', fontWeight: 'bold', fontSize: '14px', cursor: 'pointer',
  },
  empty: { textAlign: 'center', padding: '60px 20px' },
  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px',
  },
  card: { cursor: 'pointer' },
  thumbWrap: { position: 'relative', width: '100%', paddingBottom: '100%', borderRadius: '12px', overflow: 'hidden', background: '#eee' },
  thumb: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' },
  thumbPlaceholder: {
    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px', background: '#f0f0f0',
  },
  badge: {
    position: 'absolute', top: '6px', right: '6px',
    padding: '2px 6px', borderRadius: '10px', color: '#fff', fontSize: '10px', fontWeight: 'bold',
  },
  deleteBtn: {
    position: 'absolute', top: '6px', left: '6px',
    background: 'rgba(0,0,0,0.55)', border: 'none', borderRadius: '8px',
    color: '#fff', fontSize: '14px', padding: '4px 6px', cursor: 'pointer',
    zIndex: 2,
  },
  cardTitle: {
    fontSize: '12px', fontWeight: 'bold', color: '#2c3e50',
    margin: '6px 0 0', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
  },
};
